import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { dialogueVoices, type Gloss, type LessonBlock, type Locale } from '@diana/shared'
import { Markdown } from '../Markdown.js'
import { Icon } from '../../../classroom/icons.js'
import { speak, speakable, stopSpeaking, usePolishVoice } from '../../../lib/speak.js'
import type { PlayerStrings } from './copy.js'

/**
 * „🇺🇦 «и» · 🇷🇺 «ы»” inside running text: only the student's language
 * stays. A Polish student keeps both, as written.
 */
export function localizeInline(md: string, locale: Locale): string {
  if (locale === 'uk') return md.replace(/ · 🇷🇺 (?:(?! — )[^:;\n])*/g, '').replace(/🇺🇦 /g, '')
  if (locale === 'ru') return md.replace(/🇺🇦 (?:(?! · 🇷🇺)[^\n])*? · 🇷🇺 /g, '').replace(/🇷🇺 /g, '')
  return md
}

/*
 * One block of lesson text, drawn in the classroom style. The explanation
 * shows the student's language first with Polish one tap away; dialogues are
 * speech bubbles whose extra words open their gloss on tap.
 */

export function Blocks({
  blocks,
  glosses,
  locale,
  t,
}: {
  blocks: LessonBlock[]
  glosses: Gloss[]
  locale: Locale
  t: PlayerStrings
}) {
  const [open, setOpen] = useState<Gloss | null>(null)
  return (
    <div className="flex flex-col gap-6">
      {blocks.map((b, i) => (
        <Block key={i} block={b} glosses={glosses} locale={locale} t={t} onGloss={setOpen} />
      ))}
      {open && <GlossPanel gloss={open} locale={locale} t={t} onClose={() => setOpen(null)} />}
    </div>
  )
}

function Block({
  block,
  glosses,
  locale,
  t,
  onGloss,
}: {
  block: LessonBlock
  glosses: Gloss[]
  locale: Locale
  t: PlayerStrings
  onGloss: (g: Gloss) => void
}) {
  switch (block.kind) {
    case 'explain':
      return <Explain block={block} locale={locale} t={t} />
    case 'table':
      return <Markdown variant="classroom">{localizeInline(block.md, locale)}</Markdown>
    case 'mistakes':
      return (
        <section className="flex flex-col gap-2">
          <p className="m-0 text-class-label font-semibold text-class-ink-faint">{block.title}</p>
          <ul className="m-0 flex list-none flex-col gap-2 p-0 text-class-gloss text-class-ink-soft">
            {block.items.map((item, i) => (
              <li key={i} className="[&_p]:my-0">
                <Markdown variant="classroom">{localizeInline(item, locale)}</Markdown>
              </li>
            ))}
          </ul>
        </section>
      )
    case 'callout':
      return (
        <aside className="rounded-class-md bg-class-gold-100/60 px-5 py-4 shadow-[inset_0_0_0_1px_var(--color-class-gold-line)]">
          <p className="m-0 font-bold text-class-gold-text">{block.title}</p>
          {block.md && (
            <div className="[&>*:first-child]:mt-1 [&>*:last-child]:mb-0">
              <Markdown variant="classroom">{localizeInline(block.md, locale)}</Markdown>
            </div>
          )}
        </aside>
      )
    case 'dialogue':
      return <Dialogue block={block} glosses={glosses} onGloss={onGloss} listen={t.words.listen} />
    case 'glosses':
      return <GlossList title={block.title} note={block.note} items={block.items} locale={locale} />
    case 'markdown':
      return (
        <div className="[&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
          <Markdown variant="classroom">{localizeInline(block.md, locale)}</Markdown>
        </div>
      )
  }
}

/** In the student's language; Polish one tap away. A Polish student reads Polish only. */
function Explain({ block, locale, t }: { block: Extract<LessonBlock, { kind: 'explain' }>; locale: Locale; t: PlayerStrings }) {
  const [showPl, setShowPl] = useState(false)
  const own = locale === 'uk' ? block.uk : locale === 'ru' ? block.ru : block.pl
  const text = own || block.pl || block.uk || block.ru
  const canToggle = locale !== 'pl' && Boolean(own) && Boolean(block.pl)
  return (
    <section className="flex flex-col gap-2.5">
      <div className="[&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
        <Markdown variant="classroom">{text}</Markdown>
      </div>
      {canToggle && (
        <>
          <button type="button" onClick={() => setShowPl((v) => !v)} className="cl-link self-start text-class-gloss" aria-expanded={showPl}>
            {showPl ? t.explain.hidePl : t.explain.showPl}
          </button>
          {showPl && (
            <div lang="pl" className="border-l-2 border-class-line pl-4 text-class-ink-soft [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
              <Markdown variant="classroom">{block.pl}</Markdown>
            </div>
          )}
        </>
      )}
    </section>
  )
}

/* ── dialogue ────────────────────────────────────────────────────────── */

/** Where each gloss phrase sits in a line, case-insensitively; longest first, no overlaps. */
type Hit = { at: number; len: number; g: Gloss }

function markGlosses(text: string, glosses: Gloss[]): Hit[] | null {
  const lower = text.toLocaleLowerCase('pl')
  const hits: Hit[] = []
  const phrases = glosses
    .map((g) => ({ g, p: g.pl.replace(/[„”"…]/g, '').replace(/\s*\(.*\)$/, '').trim() }))
    .filter((x) => x.p.length >= 3)
    .sort((a, b) => b.p.length - a.p.length)
  for (const { g, p } of phrases) {
    const at = lower.indexOf(p.toLocaleLowerCase('pl'))
    if (at < 0) continue
    if (hits.some((h) => at < h.at + h.len && h.at < at + p.length)) continue
    hits.push({ at, len: p.length, g })
  }
  if (hits.length === 0) return null
  hits.sort((a, b) => a.at - b.at)
  return hits
}

function Line({ text, glosses, onGloss }: { text: string; glosses: Gloss[]; onGloss: (g: Gloss) => void }) {
  const hits = useMemo(() => markGlosses(text, glosses), [text, glosses])
  if (!hits) return <>{text}</>
  const out: ReactNode[] = []
  let from = 0
  hits.forEach((h, i) => {
    out.push(<Fragment key={`t${i}`}>{text.slice(from, h.at)}</Fragment>)
    out.push(
      <button
        key={`g${i}`}
        type="button"
        onClick={() => onGloss(h.g)}
        className="cursor-pointer rounded-sm border-0 border-b-2 border-class-gold bg-transparent p-0 font-[inherit] text-inherit hover:bg-class-gold-100"
      >
        {text.slice(h.at, h.at + h.len)}
      </button>,
    )
    from = h.at + h.len
  })
  out.push(<Fragment key="end">{text.slice(from)}</Fragment>)
  return <>{out}</>
}

export function Dialogue({
  block,
  glosses,
  onGloss,
  listen,
}: {
  block: Extract<LessonBlock, { kind: 'dialogue' }>
  glosses: Gloss[]
  onGloss: (g: Gloss) => void
  listen: string
}) {
  const speakers = Array.from(new Set(block.lines.map((l) => l.speaker).filter(Boolean)))
  const voice = usePolishVoice()
  const voices = useMemo(() => dialogueVoices(block.lines), [block.lines])
  const [playing, setPlaying] = useState(false)
  // Each play gets a number; stopping bumps it, so the loop below ends instead of reading the next line.
  const run = useRef(0)
  useEffect(
    () => () => {
      run.current++
      stopSpeaking()
    },
    [],
  )

  const play = async () => {
    if (playing) {
      run.current++
      stopSpeaking()
      setPlaying(false)
      return
    }
    const mine = ++run.current
    setPlaying(true)
    for (const l of block.lines) {
      if (run.current !== mine) return
      const text = speakable(l.text)
      if (text) await speak(text, { voice: voices.get(l.speaker) })
    }
    if (run.current === mine) setPlaying(false)
  }

  return (
    <section className="flex flex-col gap-3.5">
      {voice && (
        <button type="button" onClick={play} className="cl-link inline-flex items-center gap-2 self-start text-class-gloss">
          <Icon name={playing ? 'pause' : 'speaker'} size={18} fill={playing} />
          {playing ? '…' : listen}
        </button>
      )}
      {block.lines.map((l, i) => {
        // Speakers alternate sides; without names, the lines themselves alternate.
        const right = l.speaker ? speakers.indexOf(l.speaker) % 2 === 1 : i % 2 === 1
        const showName = Boolean(l.speaker) && block.lines[i - 1]?.speaker !== l.speaker
        const text = l.text.replace(/\s*\[[^\]]*\]\s*/g, ' ').trim()
        return (
          <div key={i} className={['flex max-w-[82%] flex-col gap-1', right ? 'items-end self-end' : 'items-start self-start'].join(' ')}>
            {showName && <span className="text-class-label font-semibold text-class-ink-faint">{l.speaker}</span>}
            <div
              lang="pl"
              className={[
                'px-4 py-3 font-class-display text-[16px] leading-snug',
                right
                  ? 'rounded-[18px_18px_4px_18px] bg-class-field'
                  : 'rounded-[18px_18px_18px_4px] bg-class-white shadow-[inset_0_0_0_1px_var(--color-class-hairline)]',
              ].join(' ')}
            >
              <Line text={text} glosses={glosses} onGloss={onGloss} />
            </div>
          </div>
        )
      })}
    </section>
  )
}

function glossFor(g: Gloss, locale: Locale): string {
  const own = locale === 'ru' ? g.ru : g.uk
  return [g.note, locale === 'pl' ? [g.uk, g.ru].filter(Boolean).join(' · ') : own].filter(Boolean).join(' — ')
}

function GlossPanel({ gloss, locale, t, onClose }: { gloss: Gloss; locale: Locale; t: PlayerStrings; onClose: () => void }) {
  return (
    <div
      role="status"
      className="sticky bottom-3 z-10 flex items-center gap-2 rounded-class-md bg-class-white py-3 pr-2 pl-4 shadow-[inset_0_0_0_1px_var(--color-class-hairline),0_6px_24px_-12px_rgba(36,48,77,0.25)]"
    >
      <span className="flex-1 leading-snug">
        <span lang="pl" className="block font-class-display">
          {gloss.pl}
        </span>
        <span className="text-class-gloss text-class-ink-soft">{glossFor(gloss, locale)}</span>
      </span>
      <button
        type="button"
        onClick={onClose}
        aria-label={t.dialogue.close}
        className="inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-class-ink-soft"
      >
        <Icon name="close" size={18} />
      </button>
    </div>
  )
}

export function GlossList({ title, note, items, locale }: { title: string; note: string; items: Gloss[]; locale: Locale }) {
  return (
    <details className="group rounded-class-md">
      <summary className="cursor-pointer list-none text-class-gloss font-semibold text-class-sky-700 [&::-webkit-details-marker]:hidden">
        <span className="inline-block transition group-open:rotate-90">▸</span> {title}
        {note && <span className="font-normal text-class-ink-faint"> · {note}</span>}
      </summary>
      <dl className="mt-3 flex flex-col">
        {items.map((g, i) => (
          <div key={i} className="flex flex-wrap gap-x-3 border-t border-class-line py-2 last:border-b">
            <dt lang="pl" className="font-class-display">
              {g.pl}
            </dt>
            <dd className="m-0 text-class-gloss text-class-ink-soft">{glossFor(g, locale)}</dd>
          </div>
        ))}
      </dl>
    </details>
  )
}
